/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import styled from "styled-components";
import React, {ReactNode, useEffect, useMemo, useRef} from "react";
import VehicleProgramItemDetails from "../../../pages/vehiclePrograms/ItemDetails/VehicleProgramItemDetails";
import {useAppDispatch, useAppSelector} from "../../../../redux/hooks";
import {selectGlobalDialogState} from "../../../../redux/ducks/GlobalDialog/slice";
import SensorItemDetails from "../../../pages/radar/SensorRadar/ItemDetails/RadarItemDetails";
import ItemDetails from "../../../pages/camera/SensorCamera/ItemDetails/CameraItemDetails";
import ObjectDetails from "../../../pages/objectApp/ItemDetails/ObjectDetails";
import ViewModel from "../../../pages/ScenarioAnalysis/ViewModel/ViewModel";
import CoverageRequirementsStudyDetails
  from "../../../pages/CoverageRequirements/ItemDetails/CoverageRequirementsStudyDetails";
import SensorUltrasonicItemDetails from '../../../pages/ultrasonic/ItemDetails/UltrasonicItemDetails'
import SensorLidarItemDetails from '../../../pages/lidar/ItemDetails/LidarItemDetails'
import {Dialog} from '@mui/material';
import AdasProgramsItemDetails from "../../../pages/AdasPrograms/ItemDetails/AdasProgramsItemDetails";
import useGetUrlParams from "../../../../hooks/useGetUrlParams";
import {BooleanParam, useQueryParam} from "use-query-params";
import {useSearchParams} from "react-router-dom";
import {clearAdasProgramsState} from "../../../../redux/ducks/AdasPrograms/slice";
import PotentiallyMovingObjectDetails from "../../../pages/realWorldObjects/PotentiallyMovingObjects/PotentiallyMovingObjectsDetails";
import SignsRWODetails from "../../../pages/realWorldObjects/Signs/SignsRWODetails";
import MiscellaneousObjectsDetails from "../../../pages/realWorldObjects/Miscellaneous/MiscellaneousObjectsDetails";
import ViewStudyPage from "../../../pages/SensingSystemArchitectureDesign/ViewStudy/ViewStudyPage";

const defaultWidth = '90vw'
const defaultHeight = '90%'
const StyledArgosDialog = styled(Dialog)<any>((props) => ({
  "& .MuiDialog-container .MuiPaper-root": {
    // border: '1px solid red', // uncomment to see if these styles are being applied to ur nested element, and override it with !important
    minWidth: props?.minWidth ?? props?.width ?? defaultWidth,
    maxWidth: props?.maxWidth ?? props?.width ?? defaultWidth,
    height: props?.height ?? defaultHeight,
  },
}))

export interface IComponent {
  name: string
  params: any
  key?: string
}

export const GLOBAL_DIALOG_COMPONENT_MAP: any = {
  'VehicleProgramItemDetails': <VehicleProgramItemDetails/>,
  'SensorRadarItemDetails': <SensorItemDetails/>,
  'SensorCameraItemDetails': <ItemDetails/>,
  'SensorUltrasonicItemDetails': <SensorUltrasonicItemDetails/>,
  'SensorLidarItemDetails' : <SensorLidarItemDetails/>,
  'ObjectItemDetails': <ObjectDetails/>,
  'PotentiallyMovingObjectDetails': <PotentiallyMovingObjectDetails/>,
  'MiscellaneousObjectsDetails' : <MiscellaneousObjectsDetails/>,
  'SignsRWODetails' : <SignsRWODetails/>,
  'ScenarioAnalysisItemDetails': <ViewModel/>,
  'CoverageRequirements': <CoverageRequirementsStudyDetails/>,
  'AdasProgramsItemDetails': <AdasProgramsItemDetails/>,
  'SensingSystemArchitectureDesignItemDetails': <ViewStudyPage/>,
}

const getComponent = (name: string, key: string | null = null) => {
  // get component from map and add name as key prop to it
  if (name == null || name === '')
    return null
  const component = GLOBAL_DIALOG_COMPONENT_MAP[name]
  if (component == null)
    return null
  // clone the element and add key, but keep the rest of the props
  return React.cloneElement(component, {key})
}

const GlobalArgosDialog = () => {
  const dispatch = useAppDispatch()
  const name: string = useAppSelector(state => selectGlobalDialogState(state).component?.name ?? "")
  const urlComponentName = useGetUrlParams().dialogComponentName
  const finalName = name ?? urlComponentName
  const reduxParams: any = useAppSelector(state => selectGlobalDialogState(state).component?.params ?? null)
  const reduxIsOpen: boolean = useAppSelector(selectGlobalDialogState).isOpen
  const {isGlobalDialogOpen: isGlobalDialogOpenInUrl} = useGetUrlParams()
  const [queryParamGlobalDialogOpen, setQueryParamGlobalDialogOpen] = useQueryParam('isGlobalDialogOpen', BooleanParam)
  const currentUrlParams = new URLSearchParams(window.location.search);
  const [searchParams, setSearchParams] = useSearchParams();
  const [dialogContent, setDialogContent] = React.useState<ReactNode>(null);
  const paramsWhenDialogOpen = useRef<any>(null)
  const mainParams = useRef<any>(null)

  if (paramsWhenDialogOpen.current == null) {
    mainParams.current = window.location.search
  }
  useEffect(() => {
    const dialogContent = getComponent(urlComponentName ?? name, reduxParams?.key ?? null)
    if (dialogContent != null)
      setDialogContent(dialogContent)
    else
      setQueryParamGlobalDialogOpen(false)
  }, [urlComponentName, name, finalName]);
  useEffect(() => {
    if (reduxIsOpen) {
      if (reduxParams == null || currentUrlParams == null) {
        setSearchParams({...searchParams, isGlobalDialogOpen: '1', dialogComponentName: finalName})
      } else {
        setSearchParams({...searchParams, ...reduxParams, isGlobalDialogOpen: '1', dialogComponentName: finalName})
      }
    } else {
      if (reduxParams != null)
        setQueryParamGlobalDialogOpen(false)
    }
  }, [reduxIsOpen, reduxParams]);

  const isDialogOpen = useMemo(() => {
    return reduxIsOpen || isGlobalDialogOpenInUrl === '1'
  }, [reduxIsOpen, isGlobalDialogOpenInUrl]);

  useEffect(() => {
    // if not isDialogOpen, check if reduxParams are in url and if so remove them
    if (!isDialogOpen && paramsWhenDialogOpen.current != null) {
      // take current url, then remove reduxParams from it
      const currentUrlParams = new URLSearchParams(window.location.search);
      const paramsToRemove = Object.keys(reduxParams)
      paramsToRemove.forEach((param: string) => {
        currentUrlParams.delete(param)
      })
      window.history.replaceState(null, '', window.location.pathname + '?' + currentUrlParams.toString())
    } else {
      paramsWhenDialogOpen.current = reduxParams
    }
    if (!isDialogOpen) {
      if (name === 'AdasProgramsItemDetails')
        dispatch(clearAdasProgramsState())
    }
  }, [isDialogOpen]);

  return (
    <StyledArgosDialog key={`global-dialog-${isDialogOpen}`} open={isDialogOpen} onClose={null}>
      {/*<StyledArgosDialog open={isDialogOpen} onClose={null} >*/}
      <div key={`global-dialog-content ${isDialogOpen}`} style={{width: '100%', height: '100%', overflow: 'auto'}}>
        {dialogContent}
      </div>
    </StyledArgosDialog>
  )
}

export default GlobalArgosDialog